import React, { useEffect, useState } from 'react';
import { TextInput } from '@makemydeal/dr-shared-components/text-input';
import type { InterstateOnChangeCallback } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import type { TitlingLender } from '@makemydeal/dr-platform-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import { useIsLenderFieldDisabled } from '../../hooks';
import { useLenderAddressField } from './hooks/useLenderAddressField';

type LenderAddressFieldKey = keyof Pick<TitlingLender, 'line1' | 'line2' | 'city' | 'state' | 'postalCode' | 'phone'>;

type Props = {
    id: string;
    dataTestId: string;
    label: string;
    name: string;
    field: LenderAddressFieldKey;
    // Slot the input component (defaults to TextInput; PostalCodeInput / PhoneInput both extend TextInputBaseProps).
    InputComponent?: React.ComponentType<any>;
    // Extra props forwarded to the input (e.g. country="US" for PostalCodeInput).
    extraInputProps?: Record<string, unknown>;
};

const LenderAddressTextField = ({ id, dataTestId, label, name, field, InputComponent = TextInput, extraInputProps }: Props) => {
    // The error state + change handler come from the LenderAddressValidationProvider context so
    // callers don't drill them per field.
    const { lenderAddress, updateField, error, onFieldValidation } = useLenderAddressField(field);
    const storedValue = lenderAddress?.[field] ?? '';
    const [inputText, setInputText] = useState(storedValue);
    const isLenderFieldDisabled = useIsLenderFieldDisabled();

    useEffect(() => {
        setInputText(storedValue);
    }, [storedValue]);

    const handleChange: InterstateOnChangeCallback<TextInputEventValue> = (event) => {
        const value = event.target.value as string;
        setInputText(value);
        onFieldValidation?.(value);
    };

    const handleBlur = () => {
        if (inputText !== storedValue) {
            updateField(field, inputText);
        }
    };

    return (
        <InputComponent
            size="sm"
            id={id}
            data-testid={dataTestId}
            label={label}
            name={name}
            onChange={handleChange}
            onBlur={handleBlur}
            value={inputText}
            disabled={isLenderFieldDisabled}
            hasError={error?.hasError}
            errorMessage={error?.errorMessage}
            {...extraInputProps}
            selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
        />
    );
};

export default LenderAddressTextField;
